import { motion } from "motion/react";
import { Briefcase } from "lucide-react";
import { useAdminApi } from "../lib/api";
import { useI18n } from "../i18n";

interface MarketBlock {
  market: string;
  stats: { openJobs: number; totalBudgetMinor: number; postedThisWeek: number } | null;
  activity: { id: string; kind: string; createdAt: string; payload: Record<string, unknown> }[];
}

interface OverviewData {
  markets: MarketBlock[];
  email: { template: string; status: string; count: number }[];
}

/** 總覽。各市場的營運數字與寄信狀況。 */
export default function Overview() {
  const { t } = useI18n();
  const { data, loading, error } = useAdminApi<OverviewData>("/overview");

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-40 rounded-card shimmer" />
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="rounded-card nm-inset p-6 text-sm text-brand-300">{error}</p>;
  }

  const failedEmails = (data?.email ?? [])
    .filter((e) => e.status === "failed")
    .reduce((sum, e) => sum + Number(e.count), 0);

  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-4 text-sm font-semibold text-ink">{t("ov.markets")}</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {(data?.markets ?? []).map((block, index) => (
            <motion.article
              key={block.market}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="rounded-card nm p-5"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-ink">{block.market}</span>
                <Briefcase className="size-4 text-ink-muted" />
              </div>

              <dl className="mt-4 space-y-2.5 text-sm">
                <Row label={t("ov.openJobs")} value={block.stats?.openJobs ?? 0} />
                <Row label={t("ov.thisWeek")} value={block.stats?.postedThisWeek ?? 0} />
                <Row
                  label={t("ov.totalBudget")}
                  value={`US$${Math.round((block.stats?.totalBudgetMinor ?? 0) / 100).toLocaleString()}`}
                />
              </dl>

              {block.activity.length > 0 && (
                <p className="mt-4 border-t border-line pt-3 text-[11px] text-ink-muted">
                  {t("ov.latest", { kind: block.activity[0].kind })}
                </p>
              )}
            </motion.article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-4 text-sm font-semibold text-ink">{t("ov.emailHealth")}</h2>
        <div className="rounded-card nm p-5">
          {failedEmails > 0 && (
            <p className="mb-4 rounded-2xl nm-inset px-4 py-3 text-sm text-brand-300">
              {t("ov.emailFailed", { n: failedEmails })}
            </p>
          )}

          {(data?.email ?? []).length === 0 ? (
            <p className="text-sm text-ink-muted">{t("emails.empty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-ink-muted">
                  <th className="pb-2 font-medium">{t("ov.template")}</th>
                  <th className="pb-2 font-medium">{t("ov.status")}</th>
                  <th className="pb-2 text-right font-medium">{t("ov.count")}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.email ?? []).map((row) => (
                  <tr key={`${row.template}-${row.status}`} className="border-t border-line">
                    <td className="py-2.5 text-ink">{row.template}</td>
                    <td className="py-2.5">
                      <span
                        className={
                          "rounded-pill px-2.5 py-1 text-[11px] font-medium " +
                          (row.status === "sent"
                            ? "nm-inset-sm text-ink-soft"
                            : "bg-brand-600/26 text-brand-100")
                        }
                      >
                        {row.status === "sent" ? t("emails.sent") : t("emails.failed")}
                      </span>
                    </td>
                    <td className="py-2.5 text-right tabular-nums text-ink">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="font-medium tabular-nums text-ink">{value}</dd>
    </div>
  );
}
